import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { VALIDATORS } from 'app/constants';
import { AppCustomLine as CustomLine, AppArticle as Article } from 'graphql/models/extended';
import { Category } from 'graphql/models/generated';
import { DataTableResourceType } from 'models/data.model';
import { OfferBase } from 'models/offer.model';
import { SvgIconSizeMod } from 'models/layout-mod.model'
import { Template, TemplateType } from 'models/template.model';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { FormService } from 'shared/services/form.service';
import { StoreService } from 'shared/services/store.service';
import { LoadingService } from 'app/loading.service';

@Component({
  selector: 'app-add-article-to-offer',
  templateUrl: './add-article-to-offer.component.html',
  styleUrls: ['./add-article-to-offer.component.scss']
})
export class AddArticleToOfferComponent extends BaseComponent implements OnInit {
  protected readonly svgIconSizeMod = SvgIconSizeMod;
  readonly resourceType: DataTableResourceType = 'Article';

  dropdownData: Record<string, any> = {
    categories: [],
    positionTemplates: []
  };

  isCollapsed = true;

  selectedArticle!: Article;

  filters: Record<string, any> = {};

  form!: UntypedFormGroup;

  filtersForm!: UntypedFormGroup;

  formReady = false;

  position!: CustomLine;

  offer!: OfferBase;

  isDealer!: boolean;

  maxDescriptionLength = VALIDATORS.MAX_LENGTH.LV_POSITION_TEXT;

  get categoriesForm(): UntypedFormGroup {
    return this.filtersForm.controls.categories as UntypedFormGroup;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    private formService: FormService,
    private coreDataService: CoreDataService,
    private storeService: StoreService,
    private loadingService: LoadingService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    combineLatest([
      this.storeService.getCurrentOffer(),
      this.storeService.getCustomPosition(),
      this.storeService.isCurrentAccountDealer()
    ])
      .pipe(take(1))
      .subscribe(([offer, position, isDealer]: [OfferBase, CustomLine, boolean]) => {
        this.offer = offer;
        this.position = position;
        this.isDealer = isDealer;

        this.setupPositionForm();
        this.setupFiltersForm();
        this.getPositionTemplates();
      });
  }

  private setupPositionForm(): void {
    this.storeService.setFormReady(this.formReady);
    this.form = this.formService.getPositionBaseForm(this.position);
    this.form.addControl('positionTemplate', this.fb.control(null));
    this.form.addControl('text', this.fb.control('', Validators.maxLength(VALIDATORS.MAX_LENGTH.TEXTAREA)));
    this.form.addControl('selectedArticle', this.fb.control(this.selectedArticle, Validators.required));

    this.loadingService.isLoading$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isLoading: boolean): void => {
        if (isLoading) {
          this.form.disable({ emitEvent: false });
        } else {
          this.form.enable({ emitEvent: false });
        }
      });

    this.formService.detectPositionQuantityFormChanges(this.form, this.destroyed$);

    this.formReady = true;
    this.storeService.setFormReady(this.formReady);

    this.form.controls.positionTemplate.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.form.controls.text.setValue(value, { emitEvent: false });
      });
  }

  private setupFiltersForm(): void {
    this.filtersForm = this.fb.group({
      categories: this.fb.group([]),
      listPriceMin: ['', [
        Validators.min(VALIDATORS.MIN.ARTICLE_LIST_PRICE), Validators.max(VALIDATORS.MAX.ARTICLE_LIST_PRICE)
      ]],
      listPriceMax: ['', [
        Validators.min(VALIDATORS.MIN.ARTICLE_LIST_PRICE), Validators.max(VALIDATORS.MAX.ARTICLE_LIST_PRICE)
      ]],
      purchasePriceMin: ['', [
        Validators.min(VALIDATORS.MIN.ARTICLE_PURCHASE_PRICE), Validators.max(VALIDATORS.MAX.ARTICLE_PURCHASE_PRICE)
      ]],
      purchasePriceMax: ['', [
        Validators.min(VALIDATORS.MIN.ARTICLE_PURCHASE_PRICE), Validators.max(VALIDATORS.MAX.ARTICLE_PURCHASE_PRICE)
      ]]
    });

    this.coreDataService.getArticleCategories()
      .subscribe((categories: Category[]) => {
        this.setCategories(categories);

        this.detectFiltersFormChanges();
      });
  }

  private detectFiltersFormChanges(): void {
    this.filtersForm.valueChanges
      .pipe(
        filter(() => this.filtersForm.valid),
        takeUntil(this.destroyed$)
      )
      .subscribe((values) => {
        const selectedCategories: string[] = Object.keys(values.categories)
          .filter((key) => Boolean(values.categories[key]));

        this.filters = { ...values, categories: selectedCategories };
      });
  }

  private setCategories(categories: Category[]): void {
    this.dropdownData.categories = categories.sort(
      (a, b) => a.displayName.localeCompare(b.displayName)
    );
    categories.forEach((cat) => {
      this.categoriesForm.addControl(cat.displayName, this.fb.control(''));
    })
  }

  private getPositionTemplates(): void {
    this.coreDataService.getTemplates(100, TemplateType.POSITION)
      .subscribe((templates: Template[]) => {
        this.dropdownData.positionTemplates = templates;
      });
  }

  onItemSelected(item: Article): void {
    if (item) {
      this.selectedArticle = item;
      this.form.controls.selectedArticle.setValue(this.selectedArticle);
    }
  }
}
